import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, CheckCircle, Copy } from 'lucide-react';
import { BrandedSpinner } from '@/components/ui/branded-spinner';
import { toast } from 'sonner';
import paynovusLogoStacked from '@/assets/paynovus-logo-stacked-tagline.png';

export default function CustomerMFASetup() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'loading' | 'enroll'>('loading');
  const [factorId, setFactorId] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    checkAndEnroll();
  }, []);

  async function checkAndEnroll() {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      navigate('/app/login');
      return;
    }

    const { data } = await supabase.auth.mfa.listFactors();
    const verifiedFactor = data?.totp?.find(f => f.status === 'verified');

    // If arriving via "re-enroll" link, unenroll all existing factors first
    const isReenroll = new URLSearchParams(window.location.search).get('reenroll') === '1';

    if (verifiedFactor && !isReenroll) {
      // Already enrolled and not re-enrolling — go to dashboard
      navigate('/app');
      return;
    }

    // Use service role via edge function to delete factors (bypasses AAL2 requirement)
    const hasFactors = (data?.totp || []).length > 0;
    if (hasFactors) {
      await supabase.functions.invoke('self-reset-mfa');
    }

    // Start fresh enrollment
    const { data: enrollData, error: enrollErr } = await supabase.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: `PayNovus Account TOTP ${Date.now()}`,
    });

    if (enrollErr) {
      setError(enrollErr.message);
      setStep('enroll');
      return;
    }

    if (enrollData) {
      setFactorId(enrollData.id);
      setQrCode(enrollData.totp.qr_code);
      setSecret(enrollData.totp.secret);
      setStep('enroll');
    }
  }

  async function handleVerify() {
    setError(null);
    setIsVerifying(true);

    try {
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeErr) {
        setError(challengeErr.message);
        return;
      }

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: verifyCode,
      });

      if (verifyErr) {
        setError('Invalid code. Please try again.');
        setVerifyCode('');
        return;
      }

      toast.success('Two-factor authentication enabled! Please sign in again to continue.');
      // Sign out and redirect to login — cleanest way to start a fresh AAL2 session
      await supabase.auth.signOut();
      navigate('/app/login', { replace: true });
    } catch {
      setError('Verification failed. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/app/login');
  }

  if (step === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BrandedSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen-safe hero-dark flex flex-col items-center justify-center p-4 sm:p-6">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <img src={paynovusLogoStacked} alt="PayNovus" className="h-20 sm:h-24 w-auto mx-auto mb-6" />
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="h-6 w-6 text-accent" />
            <h1 className="text-2xl font-bold text-foreground">Secure Your Account</h1>
          </div>
          <p className="text-muted-foreground text-sm">
            Two-factor authentication is required. Set up your authenticator app to continue.
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {qrCode && (
          <div className="space-y-5">
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Step 1:</strong> Scan this QR code with your authenticator app
                (Authy, Google Authenticator, 1Password, etc.)
              </p>
              <div className="flex justify-center p-4 bg-white rounded-xl">
                <img src={qrCode} alt="MFA QR Code" className="w-48 h-48" />
              </div>
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Or enter manually:</strong>
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 p-2 bg-secondary/50 rounded-lg text-xs break-all font-mono text-foreground">
                  {secret}
                </code>
                <Button
                  size="icon"
                  variant="outline"
                  onClick={() => {
                    navigator.clipboard.writeText(secret);
                    toast.info('Secret copied');
                  }}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="space-y-3 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Step 2:</strong> Enter the 6-digit code from your app
              </p>
              <div className="space-y-2">
                <Label htmlFor="code" className="text-foreground">Verification Code</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  placeholder=""
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && verifyCode.length === 6) handleVerify();
                  }}
                  className="text-center text-2xl tracking-widest"
                  disabled={isVerifying}
                  autoFocus
                />
              </div>
              <Button
                className="w-full h-12 rounded-full text-base font-semibold"
                onClick={handleVerify}
                disabled={verifyCode.length !== 6 || isVerifying}
              >
                {isVerifying ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle className="mr-2 h-4 w-4" />
                    Enable 2FA
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        <div className="text-center">
          <button onClick={handleSignOut} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
}
